import { motion } from "framer-motion";
import { TEST_IDS } from "@/constants/testIds";
import { useT } from "@/i18n/LanguageContext";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Hero = () => {
  const { t } = useT();
  const trust = t("hero.trust");

  return (
    <section
      id="top"
      data-testid={TEST_IDS.hero.root}
      className="relative isolate w-full pt-28 lg:pt-32 pb-20 lg:min-h-screen lg:flex lg:items-center"
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden -z-10">
        <div className="absolute -top-40 -left-32 w-[620px] h-[620px] rounded-full bg-ice_blue/30 blur-3xl opacity-60" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-ice_blue to-transparent" />
      </div>

      <div
        className="hidden lg:block absolute top-1/2 right-0 -translate-y-1/2 z-0 pointer-events-none"
        style={{ width: "min(48vw, 720px)" }}
      >
        <img
          src="/hero-diamond.png"
          alt="Certified natural diamond selected with tweezers for private diamond sourcing"
          className="block w-full h-auto"
          style={{
            WebkitMaskImage:
              "linear-gradient(to right, transparent 0%, black 14%, black 100%)",
            maskImage:
              "linear-gradient(to right, transparent 0%, black 14%, black 100%)",
          }}
        />
      </div>

      <div className="container-luxe relative z-10 w-full">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.3, ease: [0.22, 1, 0.36, 1], delay: 0.5 }}
          className="max-w-3xl lg:max-w-2xl xl:max-w-3xl"
        >
          <div className="flex items-center gap-3 mb-8">
            <div className="h-px w-12 bg-gold" />
            <span className="overline">{t("hero.eyebrow")}</span>
          </div>

          <h1
            data-testid={TEST_IDS.hero.heading}
            className="h-display text-5xl sm:text-6xl md:text-7xl lg:text-[5.4rem] xl:text-[6rem] text-ink text-balance leading-[0.96]"
          >
            {t("hero.title1")}
            <br />
            <span className="italic font-light text-ink/90">
              {t("hero.title2")}
            </span>
          </h1>

          <p
            data-testid={TEST_IDS.hero.sub}
            className="mt-10 max-w-xl text-base md:text-lg leading-relaxed text-muted_text font-light"
          >
            {t("hero.sub")}
          </p>

          <div className="lg:hidden mt-12 -mx-6 md:-mx-10">
            <img
              src="/hero-diamond.png"
              alt="Certified natural diamond selected with tweezers for private diamond sourcing"
              className="block w-full h-auto"
            />
          </div>

          <div className="mt-12 flex flex-col sm:flex-row gap-4">
            <button
              data-testid={TEST_IDS.hero.ctaInquiry}
              onClick={() => scrollTo("contact")}
              className="group relative overflow-hidden bg-ink text-white px-8 py-4 font-mono text-[11px] uppercase tracking-[0.32em] transition-all duration-500 hover:bg-graphite"
            >
              <span className="relative z-10 flex items-center gap-3">
                {t("hero.ctaInquiry")}
                <span className="inline-block transition-transform duration-500 group-hover:translate-x-1">
                  →
                </span>
              </span>
            </button>
            <button
              data-testid={TEST_IDS.hero.ctaCollection}
              onClick={() => scrollTo("offering")}
              className="group border border-ink/30 px-8 py-4 font-mono text-[11px] uppercase tracking-[0.32em] text-ink hover:border-ink transition-all duration-500"
            >
              <span className="flex items-center gap-3">
                {t("hero.ctaCollection")}
                <span className="inline-block transition-transform duration-500 group-hover:translate-x-1">
                  ↓
                </span>
              </span>
            </button>
          </div>

          <div
            data-testid={TEST_IDS.hero.trustLine}
            className="mt-14 flex flex-wrap lg:flex-nowrap items-center gap-x-2 lg:gap-x-3 gap-y-2 font-mono text-[9px] lg:text-[10px] uppercase tracking-[0.22em] lg:tracking-[0.26em] text-muted_text/80"
          >
            {trust.map((item, i) => (
              <span key={item} className="contents">
                <span className="whitespace-nowrap">{item}</span>
                {i < trust.length - 1 && (
                  <>
                    <span className="hidden lg:inline-block h-px w-4 bg-muted_text/40 shrink-0" />
                    <span className="lg:hidden">·</span>
                  </>
                )}
              </span>
            ))}
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2, duration: 1.4 }}
        className="hidden lg:flex absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex-col items-center gap-3"
      >
        <span className="overline-soft">{t("hero.scroll")}</span>
        <div className="h-8 w-px bg-gradient-to-b from-ink/40 to-transparent" />
      </motion.div>
    </section>
  );
};
